import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

export default function ExcelUpload() {
  const navigate  = useNavigate();
  const fileRef   = useRef();
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState(null);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.xlsx') && !f.name.endsWith('.xls')) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    setFile(f); setError(null); setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('excelFile', file);
      const { data } = await api.post('/jobs/upload-excel', fd, {
        headers:{ 'Content-Type':'multipart/form-data' }
      });
      setResult(data.results);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',fontFamily:'Inter,sans-serif'}}>
      {/* Header */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'12px 24px',display:'flex',alignItems:'center',gap:'12px',}}>
        <button onClick={()=>navigate(-1)} style={backBtn}>← Back</button>
        <div>
          <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'16px'}}>📤 Upload Excel</div>
          <div style={{color:'var(--muted)',fontSize:'11px'}}>Sync PO numbers and data from filled Excel</div>
        </div>
      </div>

      <div style={{maxWidth:'600px',margin:'0 auto',padding:'24px 16px'}}>

        {/* How it works */}
        <div style={{background:'var(--bg)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'18px',marginBottom:'20px'}}>
          <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px',marginBottom:'10px'}}>📋 How it works</div>
          {['Download the Excel from Job Sheet','Send it to the dealership to fill in PO numbers','Dealership sends it back','Upload it here — app will automatically update all jobs with PO numbers, prices, and services'].map((step,i)=>(
            <div key={i} style={{display:'flex',gap:'10px',marginBottom:'8px',alignItems:'flex-start'}}>
              <div style={{width:'22px',height:'22px',background:'var(--blue)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'500',color:'#fff',flexShrink:0}}>{i+1}</div>
              <div style={{fontSize:'13px',color:'var(--text)',paddingTop:'2px'}}>{step}</div>
            </div>
          ))}
        </div>

        {/* Upload area */}
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'24px',marginBottom:'20px',boxShadow:'var(--shadow-sm)'}}>
          <div style={{fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'.8px',color:'var(--muted)',marginBottom:'12px'}}>Select Excel File</div>

          <label style={{display:'block',border:'2px dashed var(--border-md)',borderRadius:'var(--radius-lg)',padding:'32px',textAlign:'center',cursor:'pointer',background:'var(--bg)',transition:'all .15s'}}
            onMouseEnter={e=>e.currentTarget.style.borderColor='var(--blue)'}
            onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border-md)'}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleFile}/>
            <div style={{fontSize:'2.5rem',marginBottom:'10px'}}>{file?'📊':'📁'}</div>
            {file ? (
              <>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px'}}>{file.name}</div>
                <div style={{color:'var(--muted)',fontSize:'12px',marginTop:'4px'}}>{(file.size/1024).toFixed(1)} KB · Click to change</div>
              </>
            ) : (
              <>
                <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px'}}>Click to select Excel file</div>
                <div style={{color:'var(--muted)',fontSize:'12px',marginTop:'4px'}}>.xlsx or .xls files only</div>
              </>
            )}
          </label>

          {error && <div style={{background:'var(--red-bg)',border:'1px solid rgba(220,38,38,0.3)',borderRadius:'var(--radius)',padding:'10px 14px',color:'var(--red)',fontSize:'13px',marginTop:'14px'}}>⚠️ {error}</div>}

          <button onClick={handleUpload} disabled={!file||uploading} style={{width:'100%',marginTop:'16px',background:!file||uploading?'var(--border-md)':'var(--blue)',border:'none',borderRadius:'var(--radius)',padding:'13px',color:'#fff',fontWeight:'500',fontSize:'14px',cursor:!file||uploading?'not-allowed':'pointer',fontFamily:'inherit',transition:'all .2s'}}>
            {uploading?'⏳ Uploading & Syncing...':'📤 Upload & Sync Data'}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div style={{background:'var(--surface)',border:'1.5px solid var(--green)',borderRadius:'var(--radius-lg)',padding:'20px',boxShadow:'0 2px 8px rgba(22,163,74,0.08)'}}>
            <div style={{color:'var(--green)',fontWeight:'500',fontSize:'16px',marginBottom:'14px'}}>✅ Upload Complete!</div>
            <div style={{background:'var(--bg)',borderRadius:'var(--radius)',padding:'20px',textAlign:'center'}}>
              <div style={{fontSize:'2.5rem',marginBottom:'8px'}}>✅</div>
              <div style={{fontSize:'2rem',fontWeight:'600',color:'var(--green)',fontFamily:'JetBrains Mono,monospace'}}>{result.updated}</div>
              <div style={{fontSize:'13px',color:'var(--muted)',marginTop:'4px'}}>Jobs Updated</div>
            </div>
            <button onClick={()=>navigate(-1)} style={{width:'100%',marginTop:'16px',background:'var(--blue)',border:'none',borderRadius:'var(--radius)',padding:'11px',color:'#fff',fontWeight:'500',fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}>
              ← Back to Daily Sheet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const backBtn = {background:'var(--bg)',border:'1px solid var(--border)',color:'var(--muted)',borderRadius:'var(--radius)',padding:'6px 14px',cursor:'pointer',fontSize:'13px',fontFamily:'inherit'};
